import React, { useCallback, useState } from "react";
import { Card, CardContent, Typography, Box } from "@mui/material";
import ProductionLineDiagram, {
  ComponentDetail,
  ComponentState,
  DiagramComponent,
  nextState,
} from "./ProductionLineDiagram";
import { LoadingOverlay } from "../../common/LoadingOverlay";
import UnavailablePanel from "../../common/UnavailablePanel";

type Props = {
  title: string;
  basePath: string;
  components: DiagramComponent[];
  /** Optional initial state per component key; defaults to "off". */
  initialState?: Record<string, ComponentState>;
  /**
   * Live, externally-controlled state per component key. When provided the
   * diagram reflects this (read-only) instead of the manual click-to-toggle
   * behavior. Missing keys default to "off".
   */
  state?: Record<string, ComponentState>;
  /**
   * Hover-card content per component key: the rules behind that component's
   * color and the reading they were applied to. Missing keys still get a card
   * naming the component and its state, just without the explanation.
   */
  details?: Record<string, ComponentDetail>;
  /**
   * Live status is still being fetched — show a spinner over the diagram so an
   * all-grey (everything "off") render isn't mistaken for real state.
   */
  loading?: boolean;
  /**
   * This line is not ready to be shown. The reason, which is displayed over a
   * blurred copy of the artwork — no spinner, because nothing is on its way.
   * Takes precedence over `state`, `details` and `loading`.
   */
  unavailable?: string;
};

const ProductionLineDiagramCard: React.FC<Props> = ({
  title,
  basePath,
  components,
  initialState,
  state: controlledState,
  details,
  loading = false,
  unavailable,
}) => {
  const controlled = controlledState !== undefined;

  const [internalState, setInternalState] = useState<
    Record<string, ComponentState>
  >(() => {
    const next: Record<string, ComponentState> = {};
    components.forEach((c) => {
      next[c.key] = initialState?.[c.key] ?? "off";
    });
    return next;
  });

  const handleToggle = (key: string) => {
    setInternalState((prev) => ({
      ...prev,
      [key]: nextState(prev[key] ?? "off"),
    }));
  };

  const effectiveState = controlled ? controlledState! : internalState;

  // Keep the overlay up until the artwork is cached too — otherwise it clears
  // the moment status arrives and the diagram sits on black art while the
  // green/red PNGs download.
  const [imagesReady, setImagesReady] = useState(false);
  const handleImagesReady = useCallback(() => setImagesReady(true), []);

  return (
    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flex: 1, p: { xs: 2, md: 2.5 } }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
        {unavailable ? (
          <UnavailablePanel reason={unavailable}>
            {/* Nothing live underneath: the artwork sits at its "off" default
                with no chips, so there is no state to misread. */}
            <ProductionLineDiagram
              basePath={basePath}
              components={components}
              state={{}}
              showControls={false}
            />
          </UnavailablePanel>
        ) : (
          <LoadingOverlay
            show={loading || !imagesReady}
            minHeight={0}
            label="Loading live status…"
            // The "off" diagram is solid black linework — a faint scrim reads
            // as real state, so wash it out hard while the status is unknown.
            scrim="rgba(255,255,255,0.86)"
            blurPx={3}
            spinnerSize={56}
          >
            <ProductionLineDiagram
              basePath={basePath}
              components={components}
              state={effectiveState}
              details={details}
              // Live mode: chips become a read-only status legend (no toggle).
              onToggle={controlled ? undefined : handleToggle}
              onImagesReady={handleImagesReady}
            />
          </LoadingOverlay>
        )}
      </CardContent>
    </Card>
  );
};

/** Component lists, exported for reuse / future state wiring. */
/**
 * Full lamination diagram. Lam1 and Lam2 cards both render this — each
 * lamination "line" corresponds to one of the two blenders (Blender A = Lam1,
 * Blender B = Lam2) but the surrounding machinery is shared.
 */
export const LAM_COMPONENTS: DiagramComponent[] = [
  { key: "line", label: "Line" },
  { key: "densifier", label: "Densifier" },
  { key: "extruder_A", label: "Extruder A" },
  { key: "extruder_B", label: "Extruder B" },
  { key: "blender_A", label: "Blender A" },
  { key: "blender_B", label: "Blender B" },
];

export const FOAM_COMPONENTS: DiagramComponent[] = [
  { key: "extruder", label: "Extruder" },
  { key: "winder", label: "Winder" },
  { key: "blender", label: "Blender" },
  { key: "butane_high_pressure_side", label: "Butane High Pressure" },
  { key: "butane_low_pressure_side", label: "Butane Low Pressure" },
  { key: "cooling_water", label: "Cooling Water" },
];

export default ProductionLineDiagramCard;
